import { EmailPicker, type EmailImportPayload } from "@/components/wizard/EmailPicker";
import { SupportingDocumentsUploader } from "@/components/wizard/SupportingDocumentsUploader";
import { getEmailStatus } from "@/lib/api/email";
import { readSupportingDocumentsAsBase64 } from "@/lib/supporting-documents-payload";
import { cn } from "@/lib/utils";
import { MAX_SUPPORTING_DOCS, type SupportingDocInput } from "@/types";
import { Sparkles } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  completeQuestionSteps,
  QUESTION_FLOW_TEXT_SKIP,
  type QuestionFlowCompleteExtras,
  type QuestionFlowOption,
  type QuestionFlowStep,
} from "./question-flow-model";

export {
  completeQuestionSteps,
  QUESTION_FLOW_TEXT_SKIP,
  type QuestionFlowCompleteExtras,
  type QuestionFlowOption,
  type QuestionFlowStep,
  type QuestionFlowSupportingDocument,
} from "./question-flow-model";

type QuestionFlowProps = {
  steps: QuestionFlowStep[];
  defaultValue?: Record<string, string[]>;
  choice?: Record<string, string[]>;
  onComplete?: (
    answers: Record<string, string[]>,
    extras?: QuestionFlowCompleteExtras
  ) => void | Promise<void>;
  completeLabel?: string;
  className?: string;
};

function isTextStep(step: Pick<QuestionFlowStep, "inputMode">): boolean {
  return step.inputMode === "text";
}

function isFilesStep(step: Pick<QuestionFlowStep, "inputMode">): boolean {
  return step.inputMode === "files";
}

function receiptLabel(step: QuestionFlowStep, selected: string[]): string {
  if (isTextStep(step) || isFilesStep(step)) {
    const text = selected[0]?.trim() ?? "";
    if (!text || text === QUESTION_FLOW_TEXT_SKIP) return "Skipped";
    if (isFilesStep(step)) return text;
    return text;
  }
  const labels = (step.options ?? [])
    .filter((option) => selected.includes(option.id))
    .map((option) => option.label);
  if (labels.length > 0) return labels.join(", ");
  return selected.join(", ") || "—";
}

export function QuestionFlow({
  steps,
  defaultValue,
  choice,
  onComplete,
  completeLabel = "Submit",
  className,
}: QuestionFlowProps) {
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>(defaultValue ?? {});
  const [textDraft, setTextDraft] = useState("");
  const [supportingDocs, setSupportingDocs] = useState<SupportingDocInput[]>([]);
  const [emailPickerOpen, setEmailPickerOpen] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<Record<string, string[]> | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const status = await getEmailStatus();
        if (!cancelled) setEmailAvailable(status.microsoft.connected || status.imap.connected);
      } catch {
        if (!cancelled) setEmailAvailable(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleEmailImport = useCallback((payload: EmailImportPayload) => {
    const threadFile = new File(
      [payload.threadText],
      `${payload.conversation.subject || "email-thread"}.txt`,
      { type: "text/plain" }
    );
    const importedDocs: SupportingDocInput[] = [
      {
        id: `email-thread-${Date.now()}`,
        file: threadFile,
        docType: "background",
      },
      ...payload.files
        .filter(({ attachment }) => attachment.id !== payload.contractFile?.attachment.id)
        .slice(0, 4)
        .map(({ file }, index) => ({
          id: `email-attachment-${Date.now()}-${index}`,
          file,
          docType: "background" as const,
        })),
    ];
    setSupportingDocs((current) => {
      const availableSlots = Math.max(0, MAX_SUPPORTING_DOCS - current.length);
      return [...current, ...importedDocs.slice(0, availableSlots)];
    });
    setError(null);
    setEmailPickerOpen(false);
  }, []);

  const readySteps = completeQuestionSteps(steps);
  const settled = choice ?? receipt;
  if (readySteps.length === 0 && !settled) {
    return (
      <div
        data-slot="question-flow"
        data-state="loading"
        className={cn("text-sm text-[#576274] dark:text-slate-300", className)}
      >
        Preparing questions…
      </div>
    );
  }

  if (settled) {
    return (
      <div
        data-slot="question-flow"
        data-state="receipt"
        className={cn(
          "w-full max-w-lg rounded-2xl border border-[rgba(15,23,42,0.09)] bg-white p-4 text-left shadow-[var(--shadow-card)] dark:border-white/10 dark:bg-slate-900/70 dark:shadow-none",
          className
        )}
      >
        <p className="text-xs font-medium uppercase tracking-wide text-[#576274] dark:text-slate-400">
          Answers
        </p>
        <ul className="mt-3 space-y-3">
          {(readySteps.length > 0
            ? readySteps
            : Object.keys(settled).map((id) => ({
                id,
                question: id,
                options: [] as QuestionFlowOption[],
                inputMode: "choice" as const,
              }))
          ).map((step) => {
            const selected = settled[step.id] ?? [];
            return (
              <li key={step.id}>
                <p className="text-sm font-medium text-[#101828] dark:text-white">{step.question}</p>
                <p className="mt-0.5 whitespace-pre-wrap text-sm text-[#576274] dark:text-slate-300">
                  {receiptLabel(step, selected)}
                </p>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  const step = readySteps[Math.min(index, readySteps.length - 1)];
  const options = step?.options ?? [];
  if (!step || !onComplete) {
    return (
      <div data-slot="question-flow" data-state="open" className={className}>
        {readySteps.map((item) => (
          <p key={item.id} className="text-sm text-[#101828] dark:text-slate-100">
            {item.question}
          </p>
        ))}
      </div>
    );
  }

  const mode = step.selectionMode ?? "single";
  const selected = answers[step.id] ?? [];
  const min = step.minSelections ?? (mode === "multiple" ? 1 : 1);
  const max = step.maxSelections ?? (mode === "multiple" ? options.length : 1);
  const isLast = index >= readySteps.length - 1;
  const progress =
    readySteps.length === 0
      ? 0
      : ((Math.min(index, readySteps.length - 1) + 1) / readySteps.length) * 100;
  const textMode = isTextStep(step);
  const filesMode = isFilesStep(step);
  const webSearchMode = step.id === "webSearch";
  const skipResearch = webSearchMode ? (selected[0] ?? "skip") === "skip" : false;

  function advance(nextAnswers: Record<string, string[]>) {
    if (!isLast) {
      setAnswers(nextAnswers);
      setTextDraft("");
      setIndex((current) => current + 1);
      return;
    }
    void finish(nextAnswers);
  }

  function toggle(optionId: string) {
    setError(null);
    if (mode === "single") {
      advance({ ...answers, [step.id]: [optionId] });
      return;
    }
    setAnswers((current) => {
      const currentSelected = current[step.id] ?? [];
      const exists = currentSelected.includes(optionId);
      const nextSelected = exists
        ? currentSelected.filter((id) => id !== optionId)
        : currentSelected.length >= max
          ? currentSelected
          : [...currentSelected, optionId];
      return { ...current, [step.id]: nextSelected };
    });
  }

  function submitText(skip: boolean) {
    setError(null);
    const value = skip ? QUESTION_FLOW_TEXT_SKIP : textDraft.trim();
    if (!skip && !value) {
      setError("Enter a response, or skip this question.");
      return;
    }
    advance({ ...answers, [step.id]: [value] });
  }

  function submitFiles(skip: boolean) {
    setError(null);
    if (skip || supportingDocs.length === 0) {
      setSupportingDocs([]);
      advance({ ...answers, [step.id]: [QUESTION_FLOW_TEXT_SKIP] });
      return;
    }
    const label = supportingDocs.map((doc) => doc.file.name).join(", ");
    advance({ ...answers, [step.id]: [label || "attached"] });
  }

  async function finish(nextAnswers: Record<string, string[]>) {
    setPending(true);
    setError(null);
    try {
      const filesAnswer = nextAnswers.supportingDocuments?.[0];
      const extras: QuestionFlowCompleteExtras = {};
      if (
        filesAnswer &&
        filesAnswer !== QUESTION_FLOW_TEXT_SKIP &&
        supportingDocs.length > 0
      ) {
        extras.supportingDocuments = await readSupportingDocumentsAsBase64(supportingDocs);
      }
      await onComplete?.(nextAnswers, extras);
      setReceipt(nextAnswers);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not save those answers.");
    } finally {
      setPending(false);
    }
  }

  return (
    <div
      data-slot="question-flow"
      data-state="open"
      className={cn(
        "w-full max-w-lg rounded-2xl border border-[rgba(15,23,42,0.09)] bg-white p-4 text-left shadow-[var(--shadow-card)] dark:border-white/10 dark:bg-slate-900/70 dark:shadow-none",
        className
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium text-[#576274] dark:text-slate-400">
          {Math.min(index, readySteps.length - 1) + 1} / {readySteps.length}
        </span>
        {index > 0 ? (
          <button
            type="button"
            disabled={pending}
            className="text-xs font-medium text-[#7c3aed] disabled:opacity-50 dark:text-violet-300"
            onClick={() => {
              setError(null);
              const prev = readySteps[Math.max(0, index - 1)];
              const prevAnswer = prev ? answers[prev.id]?.[0] : undefined;
              setTextDraft(
                prev && isTextStep(prev) && prevAnswer && prevAnswer !== QUESTION_FLOW_TEXT_SKIP
                  ? prevAnswer
                  : ""
              );
              setIndex((current) => Math.max(0, current - 1));
            }}
          >
            Back
          </button>
        ) : (
          <span />
        )}
      </div>
      <div
        role="progressbar"
        aria-valuenow={Math.min(index, readySteps.length - 1) + 1}
        aria-valuemin={1}
        aria-valuemax={readySteps.length}
        className="mt-2 h-1 overflow-hidden rounded-full bg-[var(--surface-sunken)] dark:bg-white/10"
      >
        <div className="h-full bg-primary" style={{ width: `${progress}%` }} />
      </div>
      <p className="mt-4 text-sm font-semibold text-[#101828] dark:text-white">{step.question}</p>
      {step.description ? (
        <p className="mt-1 text-xs text-[#576274] dark:text-slate-400">{step.description}</p>
      ) : null}

      {textMode ? (
        <div className="mt-3 flex flex-col gap-3">
          <textarea
            value={textDraft}
            disabled={pending}
            rows={4}
            placeholder="Type your answer…"
            className="w-full resize-y rounded-xl border border-[rgba(15,23,42,0.09)] bg-white px-3 py-2 text-sm text-[#101828] outline-none focus:border-[rgba(147,51,245,0.45)] dark:border-white/10 dark:bg-white/5 dark:text-slate-100"
            onChange={(event) => {
              setError(null);
              setTextDraft(event.target.value);
            }}
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={pending || !textDraft.trim()}
              className="rounded-xl bg-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              onClick={() => submitText(false)}
            >
              {pending ? "Saving…" : isLast ? completeLabel : "Continue"}
            </button>
            {step.optional ? (
              <button
                type="button"
                disabled={pending}
                className="rounded-xl border border-[rgba(15,23,42,0.09)] px-3 py-2 text-sm font-medium text-[#576274] disabled:opacity-50 dark:border-white/10 dark:text-slate-300"
                onClick={() => submitText(true)}
              >
                Skip
              </button>
            ) : null}
          </div>
        </div>
      ) : filesMode ? (
        <div className="mt-3 flex flex-col gap-3">
          <SupportingDocumentsUploader
            docs={supportingDocs}
            onChange={(docs) => {
              setError(null);
              setSupportingDocs(docs);
            }}
            hideHeader
            onEmailClick={emailAvailable && !pending ? () => setEmailPickerOpen(true) : undefined}
          />
          <EmailPicker
            open={emailPickerOpen}
            onOpenChange={setEmailPickerOpen}
            destination="background"
            onImport={handleEmailImport}
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={pending || supportingDocs.length === 0}
              className="rounded-xl bg-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              onClick={() => submitFiles(false)}
            >
              {pending ? "Saving…" : isLast ? completeLabel : "Continue"}
            </button>
            {step.optional ? (
              <button
                type="button"
                disabled={pending}
                className="rounded-xl border border-[rgba(15,23,42,0.09)] px-3 py-2 text-sm font-medium text-[#576274] disabled:opacity-50 dark:border-white/10 dark:text-slate-300"
                onClick={() => submitFiles(true)}
              >
                Skip
              </button>
            ) : null}
          </div>
        </div>
      ) : webSearchMode ? (
        <div className="mt-3 flex flex-col gap-3">
          <div className="rounded-xl border border-[rgba(15,23,42,0.09)] bg-[var(--surface-sunken)] p-4 dark:border-white/10 dark:bg-slate-900/30">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-[#101828] dark:text-slate-200">
                  Skip Legal Research
                </p>
                <p className="text-xs text-[#576274] dark:text-slate-500">
                  Disable precedent research for faster analysis
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={skipResearch}
                aria-label="Skip Legal Research"
                disabled={pending}
                onClick={() => {
                  setError(null);
                  setAnswers((current) => ({
                    ...current,
                    webSearch: [skipResearch ? "enable" : "skip"],
                  }));
                }}
                className={cn(
                  "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-white disabled:opacity-50 dark:focus:ring-offset-slate-900",
                  skipResearch ? "bg-primary" : "bg-[var(--tab-segment-bg)] dark:bg-slate-700",
                )}
              >
                <span
                  className={cn(
                    "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                    skipResearch ? "translate-x-6" : "translate-x-1",
                  )}
                />
              </button>
            </div>
            {skipResearch ? (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-[rgba(234,179,8,0.35)] bg-[var(--tint-amber-bg)] p-3 dark:border-amber-500/25 dark:bg-amber-500/10">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#b54708] dark:text-amber-400" />
                <p className="text-xs text-[#b54708] dark:text-amber-200/90">
                  Research will be skipped. The review will be faster but may not include relevant
                  legal precedents or case law.
                </p>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            disabled={pending}
            className="rounded-xl bg-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
            onClick={() => {
              advance({
                ...answers,
                webSearch: [skipResearch ? "skip" : "enable"],
              });
            }}
          >
            {pending ? "Saving…" : isLast ? completeLabel : "Continue"}
          </button>
        </div>
      ) : (
        <>
          <div data-slot="option-list" className="mt-3 flex flex-col gap-2">
            {options.map((option) => {
              const active = selected.includes(option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  disabled={pending}
                  aria-pressed={active}
                  onClick={() => toggle(option.id)}
                  className={cn(
                    "rounded-xl border px-3 py-2 text-left text-sm transition-colors",
                    active
                      ? "border-[rgba(147,51,245,0.45)] bg-[rgba(147,51,245,0.08)] text-[#101828] dark:border-violet-400/50 dark:bg-violet-500/15 dark:text-white"
                      : "border-[rgba(15,23,42,0.09)] bg-white text-[#101828] hover:border-[rgba(147,51,245,0.35)] dark:border-white/10 dark:bg-white/5 dark:text-slate-100 dark:hover:bg-white/10"
                  )}
                >
                  <span className="font-medium">{option.label}</span>
                  {option.description ? (
                    <span className="mt-0.5 block text-xs text-[#576274] dark:text-slate-400">
                      {option.description}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          {mode === "multiple" ? (
            <button
              type="button"
              disabled={pending || selected.length < min}
              className="mt-4 rounded-xl bg-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              onClick={() => {
                if (!isLast) {
                  setIndex((current) => current + 1);
                  return;
                }
                void finish(answers);
              }}
            >
              {pending ? "Saving…" : isLast ? completeLabel : "Next"}
            </button>
          ) : null}
        </>
      )}

      {error ? <p className="mt-3 text-xs text-red-600 dark:text-red-300">{error}</p> : null}
    </div>
  );
}
